import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiErrorDetail } from "../lib/api";
import { BUILD_STAMP } from "../buildInfo";
import { SectionCard } from "./identityForm";
import { SUMMARY_NONE } from "../lib/settingsSummary";

const MESSAGE_MAX = 2000;

const CATEGORIES = [
  { value: "function", label: "Something doesn't work" },
  { value: "ui_ux", label: "UI / UX" },
  { value: "bug", label: "Bug" },
  { value: "other", label: "Other" },
];

/**
 * "Send feedback" card (issue #139) — bottom of Settings, identical for
 * both roles; the role itself is read from the session server-side, never
 * chosen here. This is the one place in the app whose whole job is
 * accepting free text and getting it to a human, so:
 *
 *   - Route, viewport width, user agent, and the build stamp are attached
 *     automatically (`useLocation` / `window.innerWidth` / `navigator` /
 *     `buildInfo.js`) — the user should never have to tell us where they
 *     were, only what was wrong.
 *   - Failure shows failure. This is the direct regression test named in
 *     the issue: the notification switches (now `NotificationsCard.jsx`)
 *     toast "Preferences saved" and persist nothing. A failed POST here
 *     must NEVER reach `toast.success` — see the `catch` branch below.
 *
 * IT FOLDS (#456 player, #459 coach), and it is the section with genuinely
 * nothing to report while folded: a compose box holds no state worth
 * summarising, and an unsent draft is not a setting. So the header carries
 * `SUMMARY_NONE` — an em dash — which is the settings analogue of
 * `count={0}`: an explicit nothing rather than a silent absence. See
 * `SectionCard` in identityForm.jsx for why saying nothing is not an
 * option.
 */
export default function SendFeedbackCard({ appearance } = {}) {
  const location = useLocation();
  const [category, setCategory] = useState("function");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const trimmedLength = message.trim().length;
  const overLimit = message.length > MESSAGE_MAX;
  const canSubmit = trimmedLength > 0 && !overLimit && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        category,
        message: message.trim(),
        route: location.pathname,
        app_version: BUILD_STAMP,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        viewport_w: typeof window !== "undefined" ? window.innerWidth : null,
      });
      toast.success("Feedback sent — thanks for taking the time.");
      setMessage("");
    } catch (e) {
      // A failed POST must show the failure, not a success toast — this is
      // the regression Settings.jsx's "Preferences saved" pattern must not
      // repeat (issue #139).
      if (e.response?.status === 429) {
        toast.error("Too many submissions — please try again in a bit.");
      } else {
        const detail = formatApiErrorDetail(e.response?.data?.detail) || "Feedback could not be sent. Please try again.";
        toast.error(detail);
      }
    }
    setSubmitting(false);
  };

  return (
    <SectionCard
      icon={MessageSquareText}
      title="Send feedback"
      desc="Tell us about a function, UI or UX problem — or anything else on your mind."
      collapsible
      defaultOpen={false}
      summary={SUMMARY_NONE}
      toggleTestid="settings-feedback-toggle"
      usageItem="feedback"
      testid="send-feedback-card"
      appearance={appearance}
    >
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger data-testid="feedback-category"><SelectValue /></SelectTrigger>
        <SelectContent>
          {CATEGORIES.map((c) => (
            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What happened? What did you expect instead?"
          className="min-h-[110px]"
          data-testid="feedback-message"
          aria-invalid={overLimit ? "true" : undefined}
        />
        <div
          className={`text-xs mt-1 text-right ${overLimit ? "text-red-600" : "text-slate-400"}`}
          data-testid="feedback-char-count"
        >
          {message.length} / {MESSAGE_MAX}
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Sent with your name and the screen you're on. We read these; we don't reply to them here.
      </p>

      <Button
        onClick={submit}
        disabled={!canSubmit}
        style={{ background: "#0B2E59" }}
        data-testid="feedback-submit"
      >
        {submitting ? "Sending…" : "Send feedback"}
      </Button>
    </SectionCard>
  );
}
